import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  PawPrint, Home, LogOut, Plus, DollarSign, FileText, Settings, Upload, 
  Search, Filter, Dog, Cat, Heart, Stethoscope, Users, BarChart3,
  ClipboardList, Building2, Calendar, TrendingUp, AlertCircle, Check,
  X, Edit, Trash2, Camera, Download, Grid3X3, Cpu, Gift, PenTool,
  FileSignature, CheckCircle, XCircle, Eye, UserPlus, ArrowLeftRight, Globe, Handshake, Bell,
  Skull, ImagePlus, UserCheck, ClipboardCheck, MapPin, ShieldAlert
} from 'lucide-react';
import TransferSystem from '@/components/TransferSystem';
import PartnershipManager from '@/components/PartnershipManager';
import PhotoUploader from '@/components/PhotoUploader';
import FormBuilder from '@/components/shelter/FormBuilder';
import DNARegistry from '@/components/shelter/DNARegistry';
import LitterManager from '@/components/shelter/LitterManager';
import { AnimalCard, AdoptionDialog, EditAnimalDialog, DeathReportDialog } from '@/components/shelter';

const ShelterDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  // State management
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [animals, setAnimals] = useState([]);
  const [kennels, setKennels] = useState([]);
  const [fosters, setFosters] = useState([]);
  const [fosterPlacements, setFosterPlacements] = useState([]);
  const [adoptionApplications, setAdoptionApplications] = useState([]);
  const [financials, setFinancials] = useState([]);
  const [donations, setDonations] = useState([]);
  
  // Transfer & Partnership state
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [selectedAnimalForTransfer, setSelectedAnimalForTransfer] = useState(null);
  const [showPartnershipDialog, setShowPartnershipDialog] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [donationSummary, setDonationSummary] = useState(null);
  const [shelterSettings, setShelterSettings] = useState({});
  const [formTemplates, setFormTemplates] = useState([]);
  const [formSubmissions, setFormSubmissions] = useState([]);
  
  // Dialog states
  const [intakeDialogOpen, setIntakeDialogOpen] = useState(false);
  const [kennelDialogOpen, setKennelDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [financialDialogOpen, setFinancialDialogOpen] = useState(false);
  const [animalDetailDialog, setAnimalDetailDialog] = useState(null);
  const [csvImportDialogOpen, setCsvImportDialogOpen] = useState(false);
  const [microchipSearchOpen, setMicrochipSearchOpen] = useState(false);
  const [donationDialogOpen, setDonationDialogOpen] = useState(false);
  const [formTemplateDialogOpen, setFormTemplateDialogOpen] = useState(false);
  const [fosterDialogOpen, setFosterDialogOpen] = useState(false);
  const [assignFosterDialogOpen, setAssignFosterDialogOpen] = useState(false);
  const [selectedFoster, setSelectedFoster] = useState(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({
    title: '', description: '', event_date: '', end_date: '', location: '',
    address: '', event_type: 'general', image_url: '', registration_url: '',
    registration_required: false, max_attendees: '', is_published: false
  });
  
  // Form states
  const [newAnimal, setNewAnimal] = useState({
    name: '', species: 'Dog', breed: '', gender: '', color: '',
    date_of_birth: '', microchip_number: '', secondary_microchip: '',
    intake_type: 'stray', intake_reason: '', health_status: 'unknown',
    temperament: '', special_needs: '', adoption_fee: '', photos: []
  });
  const [newKennel, setNewKennel] = useState({ name: '', location: '', kennel_type: 'standard', capacity: 1 });
  const [newFinancial, setNewFinancial] = useState({ type: 'income', amount: '', category: '', description: '' });
  const [newDonation, setNewDonation] = useState({
    donor_name: '', donor_email: '', donor_phone: '', donation_type: 'monetary',
    amount: '', item_description: '', estimated_value: '', campaign: '',
    is_recurring: false, is_anonymous: false, tax_deductible: true, notes: ''
  });
  const [newFormTemplate, setNewFormTemplate] = useState({
    form_type: 'adoption', title: '', description: '', require_signature: true,
    header_text: '', footer_text: '', terms_and_conditions: '',
    fields: []
  });
  const [newFoster, setNewFoster] = useState({
    name: '', email: '', phone: '', address: '', city: '', state: '', zip_code: '',
    home_type: 'house', has_yard: false, has_other_pets: false, other_pets_description: '',
    has_children: false, children_ages: '', experience_level: 'beginner',
    species_preference: [], can_handle_medical: false, can_handle_behavioral: false,
    emergency_contact: '', emergency_phone: '', notes: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [csvFile, setCsvFile] = useState(null);
  const [microchipSearchTerm, setMicrochipSearchTerm] = useState('');
  const [microchipSearchResult, setMicrochipSearchResult] = useState(null);
  const [microchipSearching, setMicrochipSearching] = useState(false);
  
  // Adoption management states
  const [adoptionDialogOpen, setAdoptionDialogOpen] = useState(false);
  const [selectedAnimalForAdoption, setSelectedAnimalForAdoption] = useState(null);
  const [adopters, setAdopters] = useState([]);
  const [adoptions, setAdoptions] = useState([]);
  const [selectedAdopter, setSelectedAdopter] = useState(null);
  const [createNewAdopter, setCreateNewAdopter] = useState(true);
  const [newAdopter, setNewAdopter] = useState({
    name: '', email: '', phone: '', address: '', city: '', state: '', zip_code: '',
    emergency_contact: '', emergency_phone: '', notes: ''
  });
  const [adoptionForm, setAdoptionForm] = useState({
    adoption_fee: '', fee_paid: false, payment_method: '', contract_signed: false,
    microchip_transferred: false, follow_up_date: '', notes: ''
  });
  
  // Animal edit/death states
  const [editAnimalDialogOpen, setEditAnimalDialogOpen] = useState(false);
  const [editingAnimal, setEditingAnimal] = useState(null);
  const [deathReportDialogOpen, setDeathReportDialogOpen] = useState(false);
  const [deathReportAnimal, setDeathReportAnimal] = useState(null);
  const [deathReportForm, setDeathReportForm] = useState({
    deceased_date: '', cause_of_death: '', euthanized: false, euthanasia_reason: '',
    veterinarian: '', location: '', notes: ''
  });
  const [photoUploadDialogOpen, setPhotoUploadDialogOpen] = useState(false);
  const [photoUploadAnimal, setPhotoUploadAnimal] = useState(null);
  
  // Publish to website states
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishAnimal, setPublishAnimal] = useState(null);
  const [publishType, setPublishType] = useState('adoptable');

  // Signature canvas ref
  const signatureCanvasRef = useRef(null);

  // Fetch all data
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, animalsRes, kennelsRes, financialsRes, settingsRes, notifRes] = await Promise.all([
        axios.get('/shelter/reports/dashboard').catch(() => ({ data: null })),
        axios.get('/shelter/animals').catch(() => ({ data: [] })),
        axios.get('/shelter/kennels').catch(() => ({ data: [] })),
        axios.get('/financials').catch(() => ({ data: [] })),
        axios.get('/shelter/settings').catch(() => ({ data: {} })),
        axios.get('/notifications', { params: { unread_only: false } }).catch(() => ({ data: [] }))
      ]);
      
      setDashboardStats(statsRes.data);
      setAnimals(animalsRes.data || []);
      setKennels(kennelsRes.data || []);
      setFinancials(financialsRes.data || []);
      setShelterSettings(settingsRes.data || {});
      setNotifications(notifRes.data || []);
      setUnreadCount((notifRes.data || []).filter(n => !n.read).length);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAdoptionApplications = async () => {
    try {
      const res = await axios.get('/shelter/adoption-applications');
      setAdoptionApplications(res.data || []);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    }
  };

  const fetchDonations = async () => {
    try {
      const [donationsRes, summaryRes] = await Promise.all([
        axios.get('/shelter/donations'),
        axios.get('/shelter/donations/summary')
      ]);
      setDonations(donationsRes.data || []);
      setDonationSummary(summaryRes.data || null);
    } catch (error) {
      console.error('Failed to fetch donations:', error);
    }
  };

  const fetchFormTemplates = async () => {
    try {
      const [templatesRes, submissionsRes] = await Promise.all([
        axios.get('/shelter/form-templates'),
        axios.get('/shelter/form-submissions')
      ]);
      setFormTemplates(templatesRes.data || []);
      setFormSubmissions(submissionsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch form templates:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const res = await axios.get('/shelter/events');
      setEvents(res.data || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const fetchFosters = async () => {
    try {
      const [fostersRes, placementsRes] = await Promise.all([
        axios.get('/shelter/fosters'),
        axios.get('/shelter/foster-placements')
      ]);
      setFosters(fostersRes.data || []);
      setFosterPlacements(placementsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch fosters:', error);
    }
  };

  const fetchAdopters = async () => {
    try {
      const response = await axios.get('/shelter/adopters');
      setAdopters(response.data || []);
    } catch (error) {
      console.error('Failed to fetch adopters:', error);
    }
  };

  const fetchAdoptions = async () => {
    try {
      const response = await axios.get('/shelter/adoptions');
      setAdoptions(response.data || []);
    } catch (error) {
      console.error('Failed to fetch adoptions:', error);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    if (activeTab === 'adoptions') {
      fetchAdoptionApplications();
      fetchAdopters();
      fetchAdoptions();
    } else if (activeTab === 'financials') {
      fetchDonations();
    } else if (activeTab === 'forms') {
      fetchFormTemplates();
    } else if (activeTab === 'fosters') {
      fetchFosters();
    } else if (activeTab === 'events') {
      fetchEvents();
    }
  }, [activeTab]);

  // Foster handlers
  const handleAddFoster = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/shelter/fosters', newFoster);
      toast.success('Foster parent added!');
      setFosterDialogOpen(false);
      setNewFoster({
        name: '', email: '', phone: '', address: '', city: '', state: '', zip_code: '',
        home_type: 'house', has_yard: false, has_other_pets: false, other_pets_description: '',
        has_children: false, children_ages: '', experience_level: 'beginner',
        species_preference: [], can_handle_medical: false, can_handle_behavioral: false,
        emergency_contact: '', emergency_phone: '', notes: ''
      });
      fetchFosters();
    } catch (error) {
      toast.error('Failed to add foster');
    }
  };

  const handleAssignAnimalToFoster = async (fosterId, animalId) => {
    try {
      await axios.post(`/shelter/fosters/${fosterId}/assign-animal?animal_id=${animalId}`);
      toast.success('Animal assigned to foster!');
      fetchFosters();
      fetchDashboardData();
      setAssignFosterDialogOpen(false);
    } catch (error) {
      toast.error('Failed to assign animal');
    }
  };

  const handleReturnFromFoster = async (fosterId, animalId) => {
    try {
      await axios.post(`/shelter/fosters/${fosterId}/return-animal?animal_id=${animalId}`);
      toast.success('Animal returned from foster!');
      fetchFosters();
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to return animal');
    }
  };

  // Microchip search handler
  const handleMicrochipSearch = async () => {
    if (!microchipSearchTerm.trim()) return;
    
    setMicrochipSearching(true);
    setMicrochipSearchResult(null);
    
    try {
      const res = await axios.get(`/shelter/microchip-search/${microchipSearchTerm.trim()}`);
      setMicrochipSearchResult(res.data);
    } catch (error) {
      toast.error('Failed to search microchip');
    } finally {
      setMicrochipSearching(false);
    }
  };

  // Animal intake handler
  const handleAnimalIntake = async (e) => {
    e.preventDefault();
    try {
      // Extract photo URLs for storage
      const photoUrls = (newAnimal.photos || []).map(p => ({
        url: p.url,
        name: p.name,
        type: p.type,
        source: p.source,
        uploadedAt: p.uploadedAt
      }));
      
      await axios.post('/shelter/animals/intake', {
        ...newAnimal,
        photos: photoUrls,
        intake_date: new Date().toISOString(),
        adoption_fee: parseFloat(newAnimal.adoption_fee) || 0
      });
      toast.success('Animal intake successful!');
      setIntakeDialogOpen(false);
      setNewAnimal({
        name: '', species: 'Dog', breed: '', gender: '', color: '',
        date_of_birth: '', microchip_number: '', secondary_microchip: '',
        intake_type: 'stray', intake_reason: '', health_status: 'unknown',
        temperament: '', special_needs: '', adoption_fee: '', photos: []
      });
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add animal');
    }
  };

  // Kennel handler
  const handleAddKennel = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/shelter/kennels', newKennel);
      toast.success('Kennel added!');
      setKennelDialogOpen(false);
      setNewKennel({ name: '', location: '', kennel_type: 'standard', capacity: 1 });
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to add kennel');
    }
  };

  // Financial record handler
  const handleAddFinancial = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/financials', {
        ...newFinancial,
        amount: parseFloat(newFinancial.amount),
        date: new Date().toISOString()
      });
      toast.success('Financial record added!');
      setFinancialDialogOpen(false);
      setNewFinancial({ type: 'income', amount: '', category: '', description: '' });
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to add financial record');
    }
  };

  // Donation handler
  const handleAddDonation = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/shelter/donations', {
        ...newDonation,
        amount: parseFloat(newDonation.amount) || 0,
        estimated_value: parseFloat(newDonation.estimated_value) || 0,
        donation_date: new Date().toISOString()
      });
      toast.success('Donation recorded!');
      setDonationDialogOpen(false);
      setNewDonation({
        donor_name: '', donor_email: '', donor_phone: '', donation_type: 'monetary',
        amount: '', item_description: '', estimated_value: '', campaign: '',
        is_recurring: false, is_anonymous: false, tax_deductible: true, notes: ''
      });
      fetchDonations();
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to record donation');
    }
  };

  // Form template handler
  const handleCreateFormTemplate = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/shelter/form-templates', newFormTemplate);
      toast.success('Form template created!');
      setFormTemplateDialogOpen(false);
      setNewFormTemplate({
        form_type: 'adoption', title: '', description: '', require_signature: true,
        header_text: '', footer_text: '', terms_and_conditions: '', fields: []
      });
      fetchFormTemplates();
    } catch (error) {
      toast.error('Failed to create form template');
    }
  };

  // Logo upload handler
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post('/shelter/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Logo uploaded successfully!');
      setShelterSettings(prev => ({ ...prev, logo_url: res.data.logo_url }));
    } catch (error) {
      toast.error('Failed to upload logo');
    }
  };

  // CSV import handler
  const handleCsvImport = async (e) => {
    e.preventDefault();
    if (!csvFile) return;
    
    const formData = new FormData();
    formData.append('file', csvFile);
    
    try {
      const res = await axios.post('/shelter/animals/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Imported ${res.data.imported} animals!`);
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} errors occurred`);
      }
      setCsvImportDialogOpen(false);
      setCsvFile(null);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    }
  };

  // Download CSV template
  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get('/shelter/csv-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'animal_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Publish animal to website
  const handlePublishToWebsite = async (animal) => {
    // Open the publish dialog
    setPublishAnimal(animal);
    setPublishType('adoptable');
    setPublishDialogOpen(true);
  };
  
  const confirmPublishToWebsite = async () => {
    if (!publishAnimal) return;
    try {
      await axios.put(`/shelter/animals/${publishAnimal.animal_id}/publish-to-website`, {
        publish_type: publishType,
        description: publishAnimal.temperament || '',
        photos: publishAnimal.photos || []
      });
      toast.success(`${publishAnimal.name} published to website as ${publishType}!`);
      setPublishDialogOpen(false);
      setPublishAnimal(null);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish');
    }
  };

  // Unpublish animal from website
  const handleUnpublishFromWebsite = async (animal) => {
    try {
      await axios.put(`/shelter/animals/${animal.animal_id}/unpublish-from-website`);
      toast.success(`${animal.name} removed from website`);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unpublish');
    }
  };

  // Delete animal
  const handleDeleteAnimal = async (animal) => {
    if (!window.confirm(`Are you sure you want to permanently delete ${animal.name}? This action cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`/shelter/animals/${animal.animal_id}`);
      toast.success(`${animal.name} has been deleted`);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete animal');
    }
  };

  // Assign animal to kennel
  const handleAssignKennel = async (kennelId, animalId) => {
    try {
      await axios.put(`/shelter/kennels/${kennelId}/assign?animal_id=${animalId}`);
      toast.success('Animal assigned to kennel');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to assign animal');
    }
  };

  // Event handlers
  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/shelter/events', newEvent);
      toast.success('Event created!');
      setEventDialogOpen(false);
      setNewEvent({
        title: '', description: '', event_date: '', end_date: '', location: '',
        address: '', event_type: 'general', image_url: '', registration_url: '',
        registration_required: false, max_attendees: '', is_published: false
      });
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    }
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    if (!editingEvent) return;
    try {
      await axios.put(`/shelter/events/${editingEvent.event_id}`, editingEvent);
      toast.success('Event updated!');
      setEditingEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await axios.delete(`/shelter/events/${eventId}`);
      toast.success('Event deleted');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handlePublishEvent = async (eventId, publish) => {
    try {
      await axios.put(`/shelter/events/${eventId}/${publish ? 'publish' : 'unpublish'}`);
      toast.success(publish ? 'Event published to website!' : 'Event unpublished');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to update event');
    }
  };

  // Review adoption application
  const handleReviewApplication = async (appId, decision) => {
    try {
      await axios.put(`/shelter/adoption-applications/${appId}/review?decision=${decision}&notes=Reviewed by shelter`);
      toast.success(`Application ${decision}`);
      fetchAdoptionApplications();
    } catch (error) {
      toast.error('Failed to review application');
    }
  };

  // ==================== ADOPTION MANAGEMENT ====================
  
  const openAdoptionDialog = (animal) => {
    setSelectedAnimalForAdoption(animal);
    setAdoptionForm({
      adoption_fee: animal.adoption_fee || '',
      fee_paid: false,
      payment_method: '',
      contract_signed: false,
      microchip_transferred: false,
      follow_up_date: '',
      notes: ''
    });
    setNewAdopter({
      name: '', email: '', phone: '', address: '', city: '', state: '', zip_code: '',
      emergency_contact: '', emergency_phone: '', notes: ''
    });
    setSelectedAdopter(null);
    setCreateNewAdopter(true);
    setAdoptionDialogOpen(true);
  };

  const handleProcessAdoption = async () => {
    try {
      const adoptionData = {
        animal_id: selectedAnimalForAdoption.animal_id,
        ...adoptionForm
      };

      if (createNewAdopter) {
        adoptionData.adopter = newAdopter;
      } else {
        adoptionData.adopter_id = selectedAdopter;
      }

      await axios.post('/shelter/adoptions/process', adoptionData);
      toast.success('Adoption processed successfully!');
      setAdoptionDialogOpen(false);
      fetchDashboardData();
      fetchAdoptions();
      fetchAdopters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process adoption');
    }
  };

  // ==================== ANIMAL EDIT/UPDATE ====================

  const openEditAnimalDialog = (animal) => {
    setEditingAnimal({ ...animal });
    setEditAnimalDialogOpen(true);
  };

  const handleUpdateAnimal = async () => {
    try {
      const updates = { ...editingAnimal };
      delete updates.animal_id;
      delete updates.organization_id;
      delete updates.created_at;
      
      await axios.put(`/shelter/animals/${editingAnimal.animal_id}`, updates);
      toast.success('Animal updated successfully');
      setEditAnimalDialogOpen(false);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update animal');
    }
  };

  // ==================== DEATH REPORT ====================

  const openDeathReportDialog = (animal) => {
    setDeathReportAnimal(animal);
    setDeathReportForm({
      deceased_date: new Date().toISOString().split('T')[0],
      cause_of_death: '',
      euthanized: false,
      euthanasia_reason: '',
      veterinarian: '',
      location: '',
      notes: ''
    });
    setDeathReportDialogOpen(true);
  };

  const handleReportDeath = async () => {
    try {
      await axios.post(`/shelter/animals/${deathReportAnimal.animal_id}/report-death`, deathReportForm);
      toast.success('Death reported and records updated');
      setDeathReportDialogOpen(false);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report death');
    }
  };

  // ==================== PHOTO MANAGEMENT ====================

  const openPhotoUploadDialog = (animal) => {
    setPhotoUploadAnimal(animal);
    setPhotoUploadDialogOpen(true);
  };

  const handleAddPhotos = async (photos) => {
    try {
      await axios.post(`/shelter/animals/${photoUploadAnimal.animal_id}/photos`, { photos });
      toast.success('Photos added successfully');
      setPhotoUploadDialogOpen(false);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add photos');
    }
  };

  const handleDeletePhoto = async (animalId, photoIndex) => {
    try {
      await axios.delete(`/shelter/animals/${animalId}/photos/${photoIndex}`);
      toast.success('Photo deleted');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  // Filter animals
  const filteredAnimals = animals.filter(animal => {
    const matchesSearch = animal.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         animal.species?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         animal.breed?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         animal.microchip_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || animal.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Calculate financials
  const totalIncome = financials.filter(f => f.type === 'income').reduce((sum, f) => sum + (f.amount || 0), 0);
  const totalExpenses = financials.filter(f => f.type === 'expense').reduce((sum, f) => sum + (f.amount || 0), 0);
  const totalDonations = donationSummary?.total_combined || 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fdfbf7]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      {/* Header */}
      <header data-testid="shelter-dashboard" className="bg-white border-b border-stone-100 shadow-sm">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            {shelterSettings.logo_url ? (
              <img src={shelterSettings.logo_url} alt="Shelter Logo" className="w-12 h-12 rounded-lg object-cover" />
            ) : (
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-primary" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-stone-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {shelterSettings.organization_name || user.organization_name || 'Shelter Dashboard'}
              </h1>
              <div className="flex items-center gap-2 text-sm text-stone-600">
                <span>{user.name}</span>
                {user.subscription_status === 'trial' && (
                  <Badge variant="outline" className="text-orange-600 border-orange-300">Trial</Badge>
                )}
                {user.subscription_tier === 'premium' && (
                  <Badge className="bg-primary">Premium</Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => setMicrochipSearchOpen(true)} className="rounded-full">
              <Cpu className="w-4 h-4 mr-2" />
              Search Chip
            </Button>
            <Button variant="outline" onClick={() => setSettingsDialogOpen(true)} className="rounded-full">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button variant="outline" onClick={() => setShowPartnershipDialog(true)} className="rounded-full relative">
              <Handshake className="w-4 h-4 mr-2" />
              Partnerships
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </Button>
            <Button onClick={() => navigate('/website-builder')} className="rounded-full bg-green-600 hover:bg-green-700">
              <Globe className="w-4 h-4 mr-2" />
              Website Builder
            </Button>
            <Button data-testid="logout-btn" onClick={onLogout} variant="outline" className="rounded-full">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white shadow-sm border flex-wrap">
            <TabsTrigger value="overview" className="gap-2"><BarChart3 className="w-4 h-4" />Overview</TabsTrigger>
            <TabsTrigger value="animals" className="gap-2"><PawPrint className="w-4 h-4" />Animals</TabsTrigger>
            <TabsTrigger value="litters" className="gap-2"><Users className="w-4 h-4" />Litters</TabsTrigger>
            <TabsTrigger value="kennels" className="gap-2"><Grid3X3 className="w-4 h-4" />Kennels</TabsTrigger>
            <TabsTrigger value="fosters" className="gap-2"><Users className="w-4 h-4" />Fosters</TabsTrigger>
            <TabsTrigger value="adoptions" className="gap-2"><Heart className="w-4 h-4" />Adoptions</TabsTrigger>
            <TabsTrigger value="dna" className="gap-2"><ShieldAlert className="w-4 h-4" />DNA Registry</TabsTrigger>
            <TabsTrigger value="forms" className="gap-2"><FileSignature className="w-4 h-4" />Forms</TabsTrigger>
            <TabsTrigger value="events" className="gap-2"><Calendar className="w-4 h-4" />Events</TabsTrigger>
            <TabsTrigger value="financials" className="gap-2"><DollarSign className="w-4 h-4" />Financials</TabsTrigger>
            <TabsTrigger value="reports" className="gap-2"><FileText className="w-4 h-4" />Reports</TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <Card className="bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-stone-500">Total Animals</p>
                      <p className="text-2xl font-bold text-blue-600">{dashboardStats?.animals?.total || 0}</p>
                    </div>
                    <PawPrint className="w-8 h-8 text-blue-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-50 to-white">
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-stone-500">Available</p>
                      <p className="text-2xl font-bold text-green-600">{dashboardStats?.animals?.available || 0}</p>
                    </div>
                    <Check className="w-8 h-8 text-green-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-pink-50 to-white">
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-stone-500">Adopted</p>
                      <p className="text-2xl font-bold text-pink-600">{dashboardStats?.animals?.adopted || 0}</p>
                    </div>
                    <Heart className="w-8 h-8 text-pink-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-50 to-white">
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-stone-500">In Foster</p>
                      <p className="text-2xl font-bold text-purple-600">{dashboardStats?.animals?.fostered || 0}</p>
                    </div>
                    <Users className="w-8 h-8 text-purple-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-amber-50 to-white">
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-stone-500">Donations</p>
                      <p className="text-2xl font-bold text-amber-600">${totalDonations.toFixed(0)}</p>
                    </div>
                    <Gift className="w-8 h-8 text-amber-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-teal-50 to-white">
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-stone-500">Pending Apps</p>
                      <p className="text-2xl font-bold text-teal-600">{dashboardStats?.adoptions?.pending_applications || 0}</p>
                    </div>
                    <ClipboardList className="w-8 h-8 text-teal-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Species & Kennel Overview */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Dog className="w-5 h-5" />Species Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="flex items-center gap-2"><Dog className="w-4 h-4" />Dogs</span>
                      <span className="font-bold">{dashboardStats?.animals?.dogs || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="flex items-center gap-2"><Cat className="w-4 h-4" />Cats</span>
                      <span className="font-bold">{dashboardStats?.animals?.cats || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="flex items-center gap-2"><PawPrint className="w-4 h-4" />Other</span>
                      <span className="font-bold">{dashboardStats?.animals?.other || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Grid3X3 className="w-5 h-5" />Kennel Capacity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span>Total Kennels</span>
                      <span className="font-bold">{dashboardStats?.kennels?.total || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Occupied</span>
                      <span className="font-bold text-orange-600">{dashboardStats?.kennels?.occupied || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Available</span>
                      <span className="font-bold text-green-600">{dashboardStats?.kennels?.available || 0}</span>
                    </div>
                    <div className="w-full bg-stone-200 rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all" 
                        style={{ width: `${dashboardStats?.kennels?.occupancy_rate || 0}%` }}
                      />
                    </div>
                    <p className="text-xs text-stone-500 text-center">
                      {dashboardStats?.kennels?.occupancy_rate || 0}% Occupancy
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={() => setIntakeDialogOpen(true)} className="bg-primary hover:bg-primary/90">
                    <Plus className="w-4 h-4 mr-2" />New Intake
                  </Button>
                  <Button variant="outline" onClick={() => setMicrochipSearchOpen(true)}>
                    <Cpu className="w-4 h-4 mr-2" />Search Microchip
                  </Button>
                  <Button variant="outline" onClick={() => setCsvImportDialogOpen(true)}>
                    <Upload className="w-4 h-4 mr-2" />Import CSV
                  </Button>
                  <Button variant="outline" onClick={() => setDonationDialogOpen(true)}>
                    <Gift className="w-4 h-4 mr-2" />Record Donation
                  </Button>
                  <Button variant="outline" onClick={() => setKennelDialogOpen(true)}>
                    <Grid3X3 className="w-4 h-4 mr-2" />Add Kennel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ANIMALS TAB */}
          <TabsContent value="animals" className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div className="flex gap-3 flex-1">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    placeholder="Search by name, species, breed, or microchip..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="adopted">Adopted</SelectItem>
                    <SelectItem value="fostered">Fostered</SelectItem>
                    <SelectItem value="medical_hold">Medical Hold</SelectItem>
                    <SelectItem value="deceased">Deceased</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setCsvImportDialogOpen(true)}>
                  <Upload className="w-4 h-4 mr-2" />Import
                </Button>
                <Button onClick={() => setIntakeDialogOpen(true)} className="bg-primary hover:bg-primary/90">
                  <Plus className="w-4 h-4 mr-2" />Add Animal
                </Button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredAnimals.map((animal) => (
                <AnimalCard
                  key={animal.animal_id}
                  animal={animal}
                  onViewDetails={setAnimalDetailDialog}
                  onEdit={openEditAnimalDialog}
                  onAdopt={openAdoptionDialog}
                  onTransfer={(animal) => {
                    setSelectedAnimalForTransfer(animal);
                    setShowTransferDialog(true);
                  }}
                  onReportDeath={openDeathReportDialog}
                  onUploadPhoto={openPhotoUploadDialog}
                  onPublishToWebsite={handlePublishToWebsite}
                  onUnpublishFromWebsite={handleUnpublishFromWebsite}
                  onDelete={handleDeleteAnimal}
                />
              ))}
            </div>

            {filteredAnimals.length === 0 && (
              <div className="text-center py-12">
                <PawPrint className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">No animals found</p>
                <Button onClick={() => setIntakeDialogOpen(true)} className="mt-4">
                  Add Your First Animal
                </Button>
              </div>
            )}
          </TabsContent>

          {/* LITTERS TAB */}
          <TabsContent value="litters" className="space-y-4">
            <LitterManager />
          </TabsContent>

          {/* KENNELS TAB */}
          <TabsContent value="kennels" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Kennel Management</h2>
              <Button onClick={() => setKennelDialogOpen(true)} className="bg-primary hover:bg-primary/90">
                <Plus className="w-4 h-4 mr-2" />Add Kennel
              </Button>
            </div>

            <div className="grid md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {kennels.map((kennel) => (
                <Card key={kennel.kennel_id} className={`${kennel.status === 'occupied' ? 'border-orange-200' : 'border-green-200'}`}>
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold">{kennel.name}</h3>
                      <Badge variant={kennel.status === 'available' ? 'default' : 'secondary'}>
                        {kennel.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-stone-600">{kennel.location}</p>
                    <p className="text-xs text-stone-500">Type: {kennel.kennel_type}</p>
                    {kennel.animal && (
                      <div className="mt-2 p-2 bg-stone-50 rounded">
                        <p className="text-sm font-medium">{kennel.animal.name}</p>
                        <p className="text-xs text-stone-500">{kennel.animal.species}</p>
                      </div>
                    )}
                    {kennel.status === 'available' && animals.filter(a => a.status === 'available' && !a.kennel_id).length > 0 && (
                      <Select onValueChange={(animalId) => handleAssignKennel(kennel.kennel_id, animalId)}>
                        <SelectTrigger className="mt-2 h-8 text-xs">
                          <SelectValue placeholder="Assign animal" />
                        </SelectTrigger>
                        <SelectContent>
                          {animals.filter(a => a.status === 'available' && !a.kennel_id).map(a => (
                            <SelectItem key={a.animal_id} value={a.animal_id}>{a.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {kennels.length === 0 && (
              <div className="text-center py-12">
                <Grid3X3 className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">No kennels set up yet</p>
                <Button onClick={() => setKennelDialogOpen(true)} className="mt-4">
                  Add Your First Kennel
                </Button>
              </div>
            )}
          </TabsContent>

          {/* FOSTERS TAB */}
          <TabsContent value="fosters" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Foster Management</h2>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAssignFosterDialogOpen(true)}>
                  <ArrowLeftRight className="w-4 h-4 mr-2" />Assign Animal
                </Button>
                <Button onClick={() => setFosterDialogOpen(true)} className="bg-primary hover:bg-primary/90">
                  <UserPlus className="w-4 h-4 mr-2" />Add Foster Parent
                </Button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Foster Parents List */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5" />Foster Parents ({fosters.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {fosters.length > 0 ? (
                    <div className="space-y-3">
                      {fosters.map((foster) => (
                        <div key={foster.foster_id} className="p-3 bg-stone-50 rounded-lg">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-semibold">{foster.name}</p>
                              <p className="text-sm text-stone-600">{foster.email}</p>
                              <p className="text-xs text-stone-500">{foster.phone}</p>
                              <div className="flex gap-1 mt-1">
                                <Badge variant="outline" className="text-xs">{foster.experience_level}</Badge>
                                {foster.can_handle_medical && <Badge variant="outline" className="text-xs text-green-600">Medical OK</Badge>}
                                {foster.can_handle_behavioral && <Badge variant="outline" className="text-xs text-blue-600">Behavioral OK</Badge>}
                              </div>
                            </div>
                            <Badge variant={
                              foster.status === 'available' ? 'default' :
                              foster.status === 'active' ? 'secondary' : 'outline'
                            }>
                              {foster.status}
                            </Badge>
                          </div>
                          {foster.current_animals_info?.length > 0 && (
                            <div className="mt-2 pt-2 border-t">
                              <p className="text-xs font-medium text-stone-600 mb-1">Currently Fostering:</p>
                              <div className="flex flex-wrap gap-1">
                                {foster.current_animals_info.map((animal, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs">
                                    {animal.name} ({animal.species})
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          {foster.status === 'active' && foster.current_animals?.length > 0 && (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="mt-2 h-7 text-xs"
                              onClick={() => handleReturnFromFoster(foster.foster_id, foster.current_animals[0])}
                            >
                              Return Animal
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-stone-500 text-center py-4">No foster parents yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Active Placements */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <PawPrint className="w-5 h-5" />Active Placements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {fosterPlacements.filter(p => p.status === 'active').length > 0 ? (
                    <div className="space-y-3">
                      {fosterPlacements.filter(p => p.status === 'active').map((placement) => (
                        <div key={placement.placement_id} className="p-3 bg-stone-50 rounded-lg">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-semibold">{placement.animal?.name || 'Unknown Animal'}</p>
                              <p className="text-sm text-stone-600">{placement.animal?.species} • {placement.animal?.breed}</p>
                              <p className="text-xs text-stone-500">
                                Foster: {placement.foster?.name}
                              </p>
                              <p className="text-xs text-stone-400">
                                Since: {new Date(placement.placed_date).toLocaleDateString()}
                              </p>
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleReturnFromFoster(placement.foster_id, placement.animal_id)}
                            >
                              Return
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-stone-500 text-center py-4">No active foster placements</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Animals Available for Foster */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Animals Available for Foster</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-4 gap-3">
                  {animals.filter(a => a.status === 'available').slice(0, 8).map((animal) => (
                    <div key={animal.animal_id} className="p-3 border rounded-lg">
                      <p className="font-medium">{animal.name}</p>
                      <p className="text-sm text-stone-600">{animal.species} • {animal.breed}</p>
                      <Select onValueChange={(fosterId) => handleAssignAnimalToFoster(fosterId, animal.animal_id)}>
                        <SelectTrigger className="mt-2 h-8 text-xs">
                          <SelectValue placeholder="Assign to foster" />
                        </SelectTrigger>
                        <SelectContent>
                          {fosters.filter(f => f.status === 'available').map(f => (
                            <SelectItem key={f.foster_id} value={f.foster_id}>{f.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
                {animals.filter(a => a.status === 'available').length === 0 && (
                  <p className="text-stone-500 text-center py-4">No animals available for foster</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ADOPTIONS TAB */}
          <TabsContent value="adoptions" className="space-y-4">
            <h2 className="text-xl font-semibold">Adoption Applications</h2>
            
            {adoptionApplications.length > 0 ? (
              <div className="space-y-4">
                {adoptionApplications.map((app) => (
                  <Card key={app.application_id}>
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold">{app.applicant_name}</h3>
                          <p className="text-sm text-stone-600">{app.applicant_email}</p>
                          <p className="text-sm text-stone-500">{app.applicant_phone}</p>
                          <p className="text-xs text-stone-400 mt-1">
                            Applied: {new Date(app.submitted_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge variant={
                            app.status === 'pending' ? 'outline' :
                            app.status === 'approved' ? 'default' : 'destructive'
                          }>
                            {app.status}
                          </Badge>
                          {app.status === 'pending' && (
                            <div className="flex gap-2 mt-2">
                              <Button size="sm" onClick={() => handleReviewApplication(app.application_id, 'approved')} className="h-8">
                                <Check className="w-3 h-3 mr-1" />Approve
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => handleReviewApplication(app.application_id, 'rejected')} className="h-8">
                                <X className="w-3 h-3 mr-1" />Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <ClipboardList className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">No adoption applications yet</p>
              </div>
            )}
          </TabsContent>

          {/* DNA REGISTRY TAB */}
          <TabsContent value="dna" className="space-y-4">
            <DNARegistry />
          </TabsContent>

          {/* FORMS TAB */}
          <TabsContent value="forms" className="space-y-4">
            <FormBuilder 
              formTemplates={formTemplates} 
              onUpdate={fetchDashboardData}
              websiteUrl={null}
            />
          </TabsContent>

          {/* EVENTS TAB */}
          <TabsContent value="events" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Events Calendar</h2>
              <Button onClick={() => setEventDialogOpen(true)} className="bg-primary hover:bg-primary/90">
                <Plus className="w-4 h-4 mr-2" />Add Event
              </Button>
            </div>

            {events.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {events.map(event => (
                  <Card key={event.event_id} className={`${event.is_published ? 'border-green-200' : ''}`}>
                    {event.image_url && (
                      <img src={event.image_url} alt={event.title} className="w-full h-32 object-cover" />
                    )}
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold">{event.title}</h3>
                        <Badge variant={event.is_published ? 'default' : 'secondary'}>
                          {event.is_published ? 'Published' : 'Draft'}
                        </Badge>
                      </div>
                      <div className="text-sm text-stone-600 space-y-1">
                        <p className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {new Date(event.event_date).toLocaleDateString()}
                        </p>
                        {event.location && (
                          <p className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            {event.location}
                          </p>
                        )}
                      </div>
                      {event.description && (
                        <p className="text-sm mt-2 line-clamp-2">{event.description}</p>
                      )}
                      <div className="flex gap-2 mt-4 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => setEditingEvent(event)}>
                          <Edit className="w-3 h-3 mr-1" />Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant={event.is_published ? 'secondary' : 'outline'}
                          className={event.is_published ? '' : 'text-green-600'}
                          onClick={() => handlePublishEvent(event.event_id, !event.is_published)}
                        >
                          <Globe className="w-3 h-3 mr-1" />
                          {event.is_published ? 'Unpublish' : 'Publish'}
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteEvent(event.event_id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">No events yet</p>
                <Button onClick={() => setEventDialogOpen(true)} className="mt-4">
                  Create Your First Event
                </Button>
              </div>
            )}
          </TabsContent>

          {/* FINANCIALS TAB */}
          <TabsContent value="financials" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Financial Tracking & Donations</h2>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setDonationDialogOpen(true)}>
                  <Gift className="w-4 h-4 mr-2" />Record Donation
                </Button>
                <Button onClick={() => setFinancialDialogOpen(true)} className="bg-primary hover:bg-primary/90">
                  <Plus className="w-4 h-4 mr-2" />Add Record
                </Button>
              </div>
            </div>

            <div className="grid md:grid-cols-4 gap-4 mb-6">
              <Card className="bg-green-50">
                <CardContent className="pt-4">
                  <p className="text-sm text-stone-600">Total Income</p>
                  <p className="text-2xl font-bold text-green-600">${totalIncome.toFixed(2)}</p>
                </CardContent>
              </Card>
              <Card className="bg-red-50">
                <CardContent className="pt-4">
                  <p className="text-sm text-stone-600">Total Expenses</p>
                  <p className="text-2xl font-bold text-red-600">${totalExpenses.toFixed(2)}</p>
                </CardContent>
              </Card>
              <Card className="bg-amber-50">
                <CardContent className="pt-4">
                  <p className="text-sm text-stone-600">Total Donations</p>
                  <p className="text-2xl font-bold text-amber-600">${totalDonations.toFixed(2)}</p>
                </CardContent>
              </Card>
              <Card className="bg-blue-50">
                <CardContent className="pt-4">
                  <p className="text-sm text-stone-600">Net Balance</p>
                  <p className="text-2xl font-bold text-blue-600">${(totalIncome - totalExpenses).toFixed(2)}</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Donations List */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Gift className="w-5 h-5" />Recent Donations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {donations.length > 0 ? (
                    <div className="space-y-2">
                      {donations.slice(0, 5).map((donation) => (
                        <div key={donation.donation_id} className="flex justify-between items-center p-3 bg-stone-50 rounded">
                          <div>
                            <p className="font-medium">{donation.is_anonymous ? 'Anonymous' : donation.donor_name}</p>
                            <p className="text-xs text-stone-500">
                              {donation.donation_type === 'in_kind' ? donation.item_description : 'Monetary'}
                              {donation.campaign && ` • ${donation.campaign}`}
                            </p>
                          </div>
                          <p className="font-bold text-amber-600">
                            ${(donation.donation_type === 'monetary' ? donation.amount : donation.estimated_value)?.toFixed(2)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-stone-500 text-center py-4">No donations recorded yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Transactions List */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Transactions</CardTitle>
                </CardHeader>
                <CardContent>
                  {financials.length > 0 ? (
                    <div className="space-y-2">
                      {financials.slice(0, 5).map((record) => (
                        <div key={record.record_id} className="flex justify-between items-center p-3 bg-stone-50 rounded">
                          <div>
                            <p className="font-medium">{record.category}</p>
                            <p className="text-sm text-stone-500">{record.description}</p>
                          </div>
                          <p className={`font-bold ${record.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                            {record.type === 'income' ? '+' : '-'}${record.amount?.toFixed(2)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-stone-500 text-center py-4">No financial records yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* REPORTS TAB */}
          <TabsContent value="reports" className="space-y-4">
            <h2 className="text-xl font-semibold">Reports & Analytics</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Adoption Statistics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Total Adoptions</span>
                      <span className="font-bold">{dashboardStats?.adoptions?.total || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Avg. Length of Stay</span>
                      <span className="font-bold">{dashboardStats?.adoptions?.avg_length_of_stay || 0} days</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Pending Applications</span>
                      <span className="font-bold">{dashboardStats?.adoptions?.pending_applications || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Donation Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Total Monetary</span>
                      <span className="font-bold text-amber-600">${donationSummary?.total_monetary?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>In-Kind Value</span>
                      <span className="font-bold text-amber-600">${donationSummary?.total_in_kind_value?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Unique Donors</span>
                      <span className="font-bold">{donationSummary?.unique_donors || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Recurring Donors</span>
                      <span className="font-bold">{donationSummary?.recurring_donors || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* MICROCHIP SEARCH DIALOG */}
      <Dialog open={microchipSearchOpen} onOpenChange={setMicrochipSearchOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cpu className="w-5 h-5" />Microchip Database Search
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Enter microchip number..."
                value={microchipSearchTerm}
                onChange={(e) => setMicrochipSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleMicrochipSearch()}
              />
              <Button onClick={handleMicrochipSearch} disabled={microchipSearching}>
                {microchipSearching ? 'Searching...' : 'Search'}
              </Button>
            </div>

            {microchipSearchResult && (
              <div className="space-y-4">
                {microchipSearchResult.found ? (
                  <>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="font-semibold text-green-800 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" />Microchip Found in Database
                      </p>
                    </div>

                    {microchipSearchResult.in_registry && (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">Registry Information</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <p><strong>Status:</strong> {microchipSearchResult.registry_data?.registration_status}</p>
                            <p><strong>Registered:</strong> {microchipSearchResult.registry_data?.registered_at ? new Date(microchipSearchResult.registry_data.registered_at).toLocaleDateString() : 'N/A'}</p>
                            <p><strong>Implant Date:</strong> {microchipSearchResult.registry_data?.implant_date || 'N/A'}</p>
                            <p><strong>Implanted By:</strong> {microchipSearchResult.registry_data?.implanted_by || 'N/A'}</p>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {microchipSearchResult.owner_data && (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">Owner Information</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-sm space-y-1">
                            <p><strong>Name:</strong> {microchipSearchResult.owner_data.name}</p>
                            <p><strong>Email:</strong> {microchipSearchResult.owner_data.email}</p>
                            <p><strong>Phone:</strong> {microchipSearchResult.owner_data.phone || 'N/A'}</p>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {microchipSearchResult.animal_data && (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">Animal Information</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <p><strong>Name:</strong> {microchipSearchResult.animal_data.name}</p>
                            <p><strong>Species:</strong> {microchipSearchResult.animal_data.species}</p>
                            <p><strong>Breed:</strong> {microchipSearchResult.animal_data.breed || 'N/A'}</p>
                            <p><strong>Status:</strong> {microchipSearchResult.animal_data.status}</p>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {microchipSearchResult.shelter_data && (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">Shelter Information</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-sm space-y-1">
                            <p><strong>Shelter:</strong> {microchipSearchResult.shelter_data.name}</p>
                            <p><strong>Email:</strong> {microchipSearchResult.shelter_data.email}</p>
                            <p><strong>Phone:</strong> {microchipSearchResult.shelter_data.phone || 'N/A'}</p>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </>
                ) : (
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <p className="font-semibold text-amber-800 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />Microchip Not Found
                    </p>
                    <p className="text-sm text-amber-700 mt-1">This microchip is not registered in the database.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ANIMAL INTAKE DIALOG */}
      <Dialog open={intakeDialogOpen} onOpenChange={setIntakeDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Animal Intake</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAnimalIntake} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input value={newAnimal.name} onChange={(e) => setNewAnimal({...newAnimal, name: e.target.value})} required />
              </div>
              <div>
                <Label>Species</Label>
                <Select value={newAnimal.species} onValueChange={(v) => setNewAnimal({...newAnimal, species: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Dog">Dog</SelectItem>
                    <SelectItem value="Cat">Cat</SelectItem>
                    <SelectItem value="Rabbit">Rabbit</SelectItem>
                    <SelectItem value="Bird">Bird</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Breed</Label>
                <Input value={newAnimal.breed} onChange={(e) => setNewAnimal({...newAnimal, breed: e.target.value})} />
              </div>
              <div>
                <Label>Gender</Label>
                <Select value={newAnimal.gender} onValueChange={(v) => setNewAnimal({...newAnimal, gender: v})}>
                  <SelectTrigger><SelectValue placeholder="Select gender" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Color</Label>
                <Input value={newAnimal.color} onChange={(e) => setNewAnimal({...newAnimal, color: e.target.value})} />
              </div>
              <div>
                <Label>Date of Birth</Label>
                <Input type="date" value={newAnimal.date_of_birth} onChange={(e) => setNewAnimal({...newAnimal, date_of_birth: e.target.value})} />
              </div>
              <div>
                <Label>Primary Microchip Number</Label>
                <Input value={newAnimal.microchip_number} onChange={(e) => setNewAnimal({...newAnimal, microchip_number: e.target.value})} placeholder="Primary chip" />
              </div>
              <div>
                <Label>Secondary Microchip Number</Label>
                <Input value={newAnimal.secondary_microchip} onChange={(e) => setNewAnimal({...newAnimal, secondary_microchip: e.target.value})} placeholder="Backup/secondary chip" />
              </div>
              <div>
                <Label>Intake Type</Label>
                <Select value={newAnimal.intake_type} onValueChange={(v) => setNewAnimal({...newAnimal, intake_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stray">Stray</SelectItem>
                    <SelectItem value="surrender">Owner Surrender</SelectItem>
                    <SelectItem value="transfer">Transfer</SelectItem>
                    <SelectItem value="born_in_care">Born in Care</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Health Status</Label>
                <Select value={newAnimal.health_status} onValueChange={(v) => setNewAnimal({...newAnimal, health_status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="healthy">Healthy</SelectItem>
                    <SelectItem value="sick">Sick</SelectItem>
                    <SelectItem value="injured">Injured</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Adoption Fee ($)</Label>
                <Input type="number" value={newAnimal.adoption_fee} onChange={(e) => setNewAnimal({...newAnimal, adoption_fee: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Intake Reason</Label>
              <Textarea value={newAnimal.intake_reason} onChange={(e) => setNewAnimal({...newAnimal, intake_reason: e.target.value})} />
            </div>
            <div>
              <Label>Temperament Notes</Label>
              <Textarea value={newAnimal.temperament} onChange={(e) => setNewAnimal({...newAnimal, temperament: e.target.value})} />
            </div>
            <div>
              <Label>Special Needs</Label>
              <Textarea value={newAnimal.special_needs} onChange={(e) => setNewAnimal({...newAnimal, special_needs: e.target.value})} />
            </div>
            
            {/* Photo Upload Section */}
            <div className="border-t pt-4">
              <PhotoUploader
                photos={newAnimal.photos || []}
                onPhotosChange={(photos) => setNewAnimal({...newAnimal, photos})}
                maxPhotos={10}
                showCamera={true}
                label="Photos & Documents (Camera or Upload)"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIntakeDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90">Add Animal</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* DONATION DIALOG */}
      <Dialog open={donationDialogOpen} onOpenChange={setDonationDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5" />Record Donation
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddDonation} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Donor Name</Label>
                <Input value={newDonation.donor_name} onChange={(e) => setNewDonation({...newDonation, donor_name: e.target.value})} required />
              </div>
              <div>
                <Label>Donation Type</Label>
                <Select value={newDonation.donation_type} onValueChange={(v) => setNewDonation({...newDonation, donation_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monetary">Monetary</SelectItem>
                    <SelectItem value="in_kind">In-Kind (Goods)</SelectItem>
                    <SelectItem value="sponsorship">Sponsorship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input type="email" value={newDonation.donor_email} onChange={(e) => setNewDonation({...newDonation, donor_email: e.target.value})} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={newDonation.donor_phone} onChange={(e) => setNewDonation({...newDonation, donor_phone: e.target.value})} />
              </div>
            </div>
            {newDonation.donation_type === 'monetary' ? (
              <div>
                <Label>Amount ($)</Label>
                <Input type="number" step="0.01" value={newDonation.amount} onChange={(e) => setNewDonation({...newDonation, amount: e.target.value})} required />
              </div>
            ) : (
              <>
                <div>
                  <Label>Item Description</Label>
                  <Input value={newDonation.item_description} onChange={(e) => setNewDonation({...newDonation, item_description: e.target.value})} placeholder="e.g., Dog food, blankets, toys" />
                </div>
                <div>
                  <Label>Estimated Value ($)</Label>
                  <Input type="number" step="0.01" value={newDonation.estimated_value} onChange={(e) => setNewDonation({...newDonation, estimated_value: e.target.value})} />
                </div>
              </>
            )}
            <div>
              <Label>Campaign/Fundraiser (Optional)</Label>
              <Input value={newDonation.campaign} onChange={(e) => setNewDonation({...newDonation, campaign: e.target.value})} placeholder="e.g., Holiday Drive 2024" />
            </div>
            <div className="flex gap-4">
              <div className="flex items-center gap-2">
                <Checkbox checked={newDonation.is_recurring} onCheckedChange={(c) => setNewDonation({...newDonation, is_recurring: c})} />
                <Label className="text-sm">Recurring</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={newDonation.is_anonymous} onCheckedChange={(c) => setNewDonation({...newDonation, is_anonymous: c})} />
                <Label className="text-sm">Anonymous</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={newDonation.tax_deductible} onCheckedChange={(c) => setNewDonation({...newDonation, tax_deductible: c})} />
                <Label className="text-sm">Tax Deductible</Label>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={newDonation.notes} onChange={(e) => setNewDonation({...newDonation, notes: e.target.value})} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDonationDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90">Record Donation</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* FORM TEMPLATE DIALOG */}
      <Dialog open={formTemplateDialogOpen} onOpenChange={setFormTemplateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="w-5 h-5" />Create Form Template
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateFormTemplate} className="space-y-4">
            <div>
              <Label>Form Type</Label>
              <Select value={newFormTemplate.form_type} onValueChange={(v) => setNewFormTemplate({...newFormTemplate, form_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="adoption">Adoption Application</SelectItem>
                  <SelectItem value="foster">Foster Application</SelectItem>
                  <SelectItem value="volunteer">Volunteer Application</SelectItem>
                  <SelectItem value="surrender">Surrender Form</SelectItem>
                  <SelectItem value="bill_of_sale">Bill of Sale</SelectItem>
                  <SelectItem value="medical_release">Medical Release</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Form Title</Label>
              <Input value={newFormTemplate.title} onChange={(e) => setNewFormTemplate({...newFormTemplate, title: e.target.value})} required placeholder="e.g., Adoption Application Form" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={newFormTemplate.description} onChange={(e) => setNewFormTemplate({...newFormTemplate, description: e.target.value})} placeholder="Brief description of the form" />
            </div>
            <div>
              <Label>Header Text</Label>
              <Textarea value={newFormTemplate.header_text} onChange={(e) => setNewFormTemplate({...newFormTemplate, header_text: e.target.value})} placeholder="Text to display at the top of the form" />
            </div>
            <div>
              <Label>Terms & Conditions</Label>
              <Textarea value={newFormTemplate.terms_and_conditions} onChange={(e) => setNewFormTemplate({...newFormTemplate, terms_and_conditions: e.target.value})} placeholder="Terms that must be agreed to" rows={4} />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={newFormTemplate.require_signature} onCheckedChange={(c) => setNewFormTemplate({...newFormTemplate, require_signature: c})} />
              <Label className="text-sm">Require Digital Signature</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFormTemplateDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90">Create Template</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* KENNEL DIALOG */}
      <Dialog open={kennelDialogOpen} onOpenChange={setKennelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Kennel</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddKennel} className="space-y-4">
            <div>
              <Label>Kennel Name/Number</Label>
              <Input value={newKennel.name} onChange={(e) => setNewKennel({...newKennel, name: e.target.value})} required placeholder="e.g., K-101" />
            </div>
            <div>
              <Label>Location</Label>
              <Input value={newKennel.location} onChange={(e) => setNewKennel({...newKennel, location: e.target.value})} placeholder="e.g., Building A, Row 1" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={newKennel.kennel_type} onValueChange={(v) => setNewKennel({...newKennel, kennel_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="medical">Medical/Isolation</SelectItem>
                  <SelectItem value="outdoor">Outdoor</SelectItem>
                  <SelectItem value="large">Large/Multi-pet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setKennelDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90">Add Kennel</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* FINANCIAL DIALOG */}
      <Dialog open={financialDialogOpen} onOpenChange={setFinancialDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Financial Record</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddFinancial} className="space-y-4">
            <div>
              <Label>Type</Label>
              <Select value={newFinancial.type} onValueChange={(v) => setNewFinancial({...newFinancial, type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount ($)</Label>
              <Input type="number" step="0.01" value={newFinancial.amount} onChange={(e) => setNewFinancial({...newFinancial, amount: e.target.value})} required />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={newFinancial.category} onValueChange={(v) => setNewFinancial({...newFinancial, category: v})}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Adoption Fee">Adoption Fee</SelectItem>
                  <SelectItem value="Donation">Donation</SelectItem>
                  <SelectItem value="Medical">Medical</SelectItem>
                  <SelectItem value="Food & Supplies">Food & Supplies</SelectItem>
                  <SelectItem value="Utilities">Utilities</SelectItem>
                  <SelectItem value="Salary">Salary</SelectItem>
                  <SelectItem value="Rent">Rent</SelectItem>
                  <SelectItem value="Marketing">Marketing</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={newFinancial.description} onChange={(e) => setNewFinancial({...newFinancial, description: e.target.value})} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFinancialDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90">Add Record</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CSV IMPORT DIALOG */}
      <Dialog open={csvImportDialogOpen} onOpenChange={setCsvImportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Animals from CSV</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCsvImport} className="space-y-4">
            <div className="p-4 bg-stone-50 rounded-lg">
              <p className="text-sm text-stone-600 mb-3">Upload a CSV file with animal data. Need a template?</p>
              <Button type="button" variant="outline" size="sm" onClick={handleDownloadTemplate}>
                <Download className="w-4 h-4 mr-2" />Download Template
              </Button>
            </div>
            <div>
              <Label>CSV File</Label>
              <Input type="file" accept=".csv" onChange={(e) => setCsvFile(e.target.files[0])} required />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCsvImportDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={!csvFile}>
                <Upload className="w-4 h-4 mr-2" />Import
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* SETTINGS DIALOG */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Shelter Settings & Customization</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Logo Upload */}
            <div>
              <Label className="mb-2 block">Shelter Logo</Label>
              <div className="flex items-center gap-4">
                {shelterSettings.logo_url ? (
                  <img src={shelterSettings.logo_url} alt="Logo" className="w-20 h-20 rounded-lg object-cover border" />
                ) : (
                  <div className="w-20 h-20 bg-stone-100 rounded-lg flex items-center justify-center border">
                    <Camera className="w-8 h-8 text-stone-400" />
                  </div>
                )}
                <div>
                  <Input type="file" accept="image/*" onChange={handleLogoUpload} className="max-w-xs" />
                  <p className="text-xs text-stone-500 mt-1">Upload your shelter&apos;s logo (PNG, JPG)</p>
                </div>
              </div>
            </div>

            {/* Organization Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Organization Name</Label>
                <Input 
                  value={shelterSettings.organization_name || ''} 
                  onChange={(e) => setShelterSettings({...shelterSettings, organization_name: e.target.value})} 
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input 
                  value={shelterSettings.phone || ''} 
                  onChange={(e) => setShelterSettings({...shelterSettings, phone: e.target.value})} 
                />
              </div>
              <div>
                <Label>Address</Label>
                <Input 
                  value={shelterSettings.address || ''} 
                  onChange={(e) => setShelterSettings({...shelterSettings, address: e.target.value})} 
                />
              </div>
              <div>
                <Label>City</Label>
                <Input 
                  value={shelterSettings.city || ''} 
                  onChange={(e) => setShelterSettings({...shelterSettings, city: e.target.value})} 
                />
              </div>
              <div>
                <Label>State</Label>
                <Input 
                  value={shelterSettings.state || ''} 
                  onChange={(e) => setShelterSettings({...shelterSettings, state: e.target.value})} 
                />
              </div>
              <div>
                <Label>Zip Code</Label>
                <Input 
                  value={shelterSettings.zip_code || ''} 
                  onChange={(e) => setShelterSettings({...shelterSettings, zip_code: e.target.value})} 
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea 
                value={shelterSettings.description || ''} 
                onChange={(e) => setShelterSettings({...shelterSettings, description: e.target.value})}
                placeholder="Tell us about your shelter..."
              />
            </div>

            {/* Payment Provider Selection */}
            <div className="border-t pt-4">
              <Label className="mb-2 block text-base font-semibold flex items-center gap-2">
                <DollarSign className="w-4 h-4" /> Payment Settings
              </Label>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Payment Provider</Label>
                  <Select 
                    value={shelterSettings.payment_provider || 'stripe'} 
                    onValueChange={(v) => setShelterSettings({...shelterSettings, payment_provider: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="stripe">Stripe (Recommended)</SelectItem>
                      <SelectItem value="paypal">PayPal</SelectItem>
                      <SelectItem value="razorpay">Razorpay</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-stone-500 mt-1">
                    {shelterSettings.payment_provider === 'paypal' && 'PayPal requires API credentials'}
                    {shelterSettings.payment_provider === 'razorpay' && 'Razorpay requires API credentials'}
                    {(!shelterSettings.payment_provider || shelterSettings.payment_provider === 'stripe') && 'Stripe is pre-configured'}
                  </p>
                </div>
                {shelterSettings.payment_provider && shelterSettings.payment_provider !== 'stripe' && (
                  <div>
                    <Label>API Key (Optional)</Label>
                    <Input 
                      type="password"
                      value={shelterSettings.payment_api_key || ''} 
                      onChange={(e) => setShelterSettings({...shelterSettings, payment_api_key: e.target.value})}
                      placeholder={`Enter ${shelterSettings.payment_provider} API key`}
                    />
                    <p className="text-xs text-stone-500 mt-1">Leave empty to use test mode</p>
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSettingsDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={async () => {
                  try {
                    await axios.put('/shelter/settings', shelterSettings);
                    toast.success('Settings saved!');
                    setSettingsDialogOpen(false);
                  } catch (error) {
                    toast.error('Failed to save settings');
                  }
                }} 
                className="bg-primary hover:bg-primary/90"
              >
                Save Settings
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* ANIMAL DETAIL DIALOG */}
      <Dialog open={!!animalDetailDialog} onOpenChange={() => setAnimalDetailDialog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{animalDetailDialog?.name || 'Animal Details'}</DialogTitle>
          </DialogHeader>
          {animalDetailDialog && (
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-40 h-40 bg-stone-100 rounded-lg flex items-center justify-center overflow-hidden">
                  {animalDetailDialog.photos?.length > 0 ? (
                    <img src={animalDetailDialog.photos[0]} alt={animalDetailDialog.name} className="w-full h-full object-cover" />
                  ) : (
                    <PawPrint className="w-12 h-12 text-stone-300" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <div className="flex gap-2">
                    <Badge>{animalDetailDialog.species}</Badge>
                    <Badge variant="outline">{animalDetailDialog.status?.replace('_', ' ')}</Badge>
                  </div>
                  <p><strong>Breed:</strong> {animalDetailDialog.breed || 'Unknown'}</p>
                  <p><strong>Gender:</strong> {animalDetailDialog.gender || 'Unknown'}</p>
                  <p><strong>Color:</strong> {animalDetailDialog.color || 'Unknown'}</p>
                  <p><strong>Primary Microchip:</strong> {animalDetailDialog.microchip_number || 'None'}</p>
                  {animalDetailDialog.secondary_microchip && (
                    <p><strong>Secondary Microchip:</strong> {animalDetailDialog.secondary_microchip}</p>
                  )}
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p><strong>Intake Date:</strong> {animalDetailDialog.intake_date ? new Date(animalDetailDialog.intake_date).toLocaleDateString() : 'N/A'}</p>
                  <p><strong>Intake Type:</strong> {animalDetailDialog.intake_type}</p>
                  <p><strong>Health Status:</strong> {animalDetailDialog.health_status}</p>
                </div>
                <div>
                  <p><strong>Adoption Fee:</strong> ${animalDetailDialog.adoption_fee || 0}</p>
                  <p><strong>Spay/Neuter:</strong> {animalDetailDialog.spay_neuter_status}</p>
                </div>
              </div>
              {animalDetailDialog.temperament && (
                <div>
                  <p className="font-semibold">Temperament Notes:</p>
                  <p className="text-sm text-stone-600">{animalDetailDialog.temperament}</p>
                </div>
              )}
              {animalDetailDialog.special_needs && (
                <div>
                  <p className="font-semibold">Special Needs:</p>
                  <p className="text-sm text-stone-600">{animalDetailDialog.special_needs}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* FOSTER DIALOG */}
      <Dialog open={fosterDialogOpen} onOpenChange={setFosterDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />Add Foster Parent
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddFoster} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input value={newFoster.name} onChange={(e) => setNewFoster({...newFoster, name: e.target.value})} required />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={newFoster.email} onChange={(e) => setNewFoster({...newFoster, email: e.target.value})} required />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input value={newFoster.phone} onChange={(e) => setNewFoster({...newFoster, phone: e.target.value})} required />
              </div>
              <div>
                <Label>Experience Level</Label>
                <Select value={newFoster.experience_level} onValueChange={(v) => setNewFoster({...newFoster, experience_level: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="experienced">Experienced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Address</Label>
                <Input value={newFoster.address} onChange={(e) => setNewFoster({...newFoster, address: e.target.value})} />
              </div>
              <div>
                <Label>City</Label>
                <Input value={newFoster.city} onChange={(e) => setNewFoster({...newFoster, city: e.target.value})} />
              </div>
              <div>
                <Label>State</Label>
                <Input value={newFoster.state} onChange={(e) => setNewFoster({...newFoster, state: e.target.value})} />
              </div>
              <div>
                <Label>Zip Code</Label>
                <Input value={newFoster.zip_code} onChange={(e) => setNewFoster({...newFoster, zip_code: e.target.value})} />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Home Type</Label>
                <Select value={newFoster.home_type} onValueChange={(v) => setNewFoster({...newFoster, home_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="house">House</SelectItem>
                    <SelectItem value="apartment">Apartment</SelectItem>
                    <SelectItem value="condo">Condo</SelectItem>
                    <SelectItem value="townhouse">Townhouse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox checked={newFoster.has_yard} onCheckedChange={(c) => setNewFoster({...newFoster, has_yard: c})} />
                  <Label className="text-sm">Has Yard</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox checked={newFoster.has_other_pets} onCheckedChange={(c) => setNewFoster({...newFoster, has_other_pets: c})} />
                  <Label className="text-sm">Has Other Pets</Label>
                </div>
              </div>
            </div>
            {newFoster.has_other_pets && (
              <div>
                <Label>Other Pets Description</Label>
                <Input value={newFoster.other_pets_description} onChange={(e) => setNewFoster({...newFoster, other_pets_description: e.target.value})} placeholder="e.g., 2 cats, 1 dog" />
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Checkbox checked={newFoster.has_children} onCheckedChange={(c) => setNewFoster({...newFoster, has_children: c})} />
                <Label className="text-sm">Has Children</Label>
              </div>
              {newFoster.has_children && (
                <div>
                  <Label>Children Ages</Label>
                  <Input value={newFoster.children_ages} onChange={(e) => setNewFoster({...newFoster, children_ages: e.target.value})} placeholder="e.g., 5, 8, 12" />
                </div>
              )}
            </div>
            <div className="flex gap-4">
              <div className="flex items-center gap-2">
                <Checkbox checked={newFoster.can_handle_medical} onCheckedChange={(c) => setNewFoster({...newFoster, can_handle_medical: c})} />
                <Label className="text-sm">Can Handle Medical Cases</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={newFoster.can_handle_behavioral} onCheckedChange={(c) => setNewFoster({...newFoster, can_handle_behavioral: c})} />
                <Label className="text-sm">Can Handle Behavioral Cases</Label>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Emergency Contact Name</Label>
                <Input value={newFoster.emergency_contact} onChange={(e) => setNewFoster({...newFoster, emergency_contact: e.target.value})} />
              </div>
              <div>
                <Label>Emergency Contact Phone</Label>
                <Input value={newFoster.emergency_phone} onChange={(e) => setNewFoster({...newFoster, emergency_phone: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={newFoster.notes} onChange={(e) => setNewFoster({...newFoster, notes: e.target.value})} placeholder="Any additional notes about the foster parent" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFosterDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90">Add Foster Parent</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ASSIGN FOSTER DIALOG */}
      <Dialog open={assignFosterDialogOpen} onOpenChange={setAssignFosterDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5" />Assign Animal to Foster
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Animal</Label>
              <Select onValueChange={(animalId) => setSelectedFoster({...selectedFoster, animalId})}>
                <SelectTrigger><SelectValue placeholder="Choose an animal" /></SelectTrigger>
                <SelectContent>
                  {animals.filter(a => a.status === 'available').map(a => (
                    <SelectItem key={a.animal_id} value={a.animal_id}>{a.name} ({a.species})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Select Foster Parent</Label>
              <Select onValueChange={(fosterId) => setSelectedFoster({...selectedFoster, fosterId})}>
                <SelectTrigger><SelectValue placeholder="Choose a foster parent" /></SelectTrigger>
                <SelectContent>
                  {fosters.filter(f => f.status === 'available').map(f => (
                    <SelectItem key={f.foster_id} value={f.foster_id}>{f.name} ({f.experience_level})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAssignFosterDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={() => {
                  if (selectedFoster?.fosterId && selectedFoster?.animalId) {
                    handleAssignAnimalToFoster(selectedFoster.fosterId, selectedFoster.animalId);
                  } else {
                    toast.error('Please select both an animal and a foster parent');
                  }
                }} 
                className="bg-primary hover:bg-primary/90"
              >
                Assign to Foster
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer System Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        {selectedAnimalForTransfer && (
          <TransferSystem
            user={user}
            animal={selectedAnimalForTransfer}
            onClose={() => {
              setShowTransferDialog(false);
              setSelectedAnimalForTransfer(null);
            }}
            onTransferComplete={() => {
              fetchDashboardData();
            }}
          />
        )}
      </Dialog>

      {/* Partnership Manager Dialog */}
      <Dialog open={showPartnershipDialog} onOpenChange={setShowPartnershipDialog}>
        <PartnershipManager
          user={user}
          onClose={() => setShowPartnershipDialog(false)}
        />
      </Dialog>

      {/* ADOPTION PROCESSING DIALOG */}
      <Dialog open={adoptionDialogOpen} onOpenChange={setAdoptionDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-500" />
              Process Adoption - {selectedAnimalForAdoption?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Adopter Selection */}
            <div className="space-y-4">
              <div className="flex gap-4">
                <Button
                  variant={createNewAdopter ? 'default' : 'outline'}
                  onClick={() => setCreateNewAdopter(true)}
                  className="flex-1"
                >
                  <UserPlus className="w-4 h-4 mr-2" />New Adopter
                </Button>
                <Button
                  variant={!createNewAdopter ? 'default' : 'outline'}
                  onClick={() => setCreateNewAdopter(false)}
                  className="flex-1"
                >
                  <UserCheck className="w-4 h-4 mr-2" />Existing Adopter
                </Button>
              </div>

              {createNewAdopter ? (
                <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg bg-stone-50">
                  <h4 className="col-span-2 font-semibold text-sm">New Adopter Information</h4>
                  <div>
                    <Label>Full Name *</Label>
                    <Input value={newAdopter.name} onChange={(e) => setNewAdopter({...newAdopter, name: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input type="email" value={newAdopter.email} onChange={(e) => setNewAdopter({...newAdopter, email: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input value={newAdopter.phone} onChange={(e) => setNewAdopter({...newAdopter, phone: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Input value={newAdopter.address} onChange={(e) => setNewAdopter({...newAdopter, address: e.target.value})} />
                  </div>
                  <div>
                    <Label>City</Label>
                    <Input value={newAdopter.city} onChange={(e) => setNewAdopter({...newAdopter, city: e.target.value})} />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input value={newAdopter.state} onChange={(e) => setNewAdopter({...newAdopter, state: e.target.value})} />
                  </div>
                  <div>
                    <Label>Zip Code</Label>
                    <Input value={newAdopter.zip_code} onChange={(e) => setNewAdopter({...newAdopter, zip_code: e.target.value})} />
                  </div>
                  <div>
                    <Label>Emergency Contact</Label>
                    <Input value={newAdopter.emergency_contact} onChange={(e) => setNewAdopter({...newAdopter, emergency_contact: e.target.value})} />
                  </div>
                  <div>
                    <Label>Emergency Phone</Label>
                    <Input value={newAdopter.emergency_phone} onChange={(e) => setNewAdopter({...newAdopter, emergency_phone: e.target.value})} />
                  </div>
                  <div className="col-span-2">
                    <Label>Notes</Label>
                    <Textarea value={newAdopter.notes} onChange={(e) => setNewAdopter({...newAdopter, notes: e.target.value})} />
                  </div>
                </div>
              ) : (
                <div className="p-4 border rounded-lg">
                  <Label>Select Existing Adopter</Label>
                  <Select value={selectedAdopter} onValueChange={setSelectedAdopter}>
                    <SelectTrigger><SelectValue placeholder="Choose an adopter" /></SelectTrigger>
                    <SelectContent>
                      {adopters.map(adopter => (
                        <SelectItem key={adopter.adopter_id} value={adopter.adopter_id}>
                          {adopter.name} - {adopter.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {adopters.length === 0 && (
                    <p className="text-sm text-stone-500 mt-2">No existing adopters. Create a new one above.</p>
                  )}
                </div>
              )}
            </div>

            {/* Adoption Details */}
            <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg">
              <h4 className="col-span-2 font-semibold text-sm">Adoption Details</h4>
              <div>
                <Label>Adoption Fee ($)</Label>
                <Input 
                  type="number" 
                  value={adoptionForm.adoption_fee} 
                  onChange={(e) => setAdoptionForm({...adoptionForm, adoption_fee: e.target.value})} 
                />
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select value={adoptionForm.payment_method} onValueChange={(v) => setAdoptionForm({...adoptionForm, payment_method: v})}>
                  <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Credit/Debit Card</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="online">Online Payment</SelectItem>
                    <SelectItem value="waived">Fee Waived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Follow-up Date</Label>
                <Input type="date" value={adoptionForm.follow_up_date} onChange={(e) => setAdoptionForm({...adoptionForm, follow_up_date: e.target.value})} />
              </div>
              <div className="flex flex-col gap-3 justify-end">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={adoptionForm.fee_paid} 
                    onCheckedChange={(c) => setAdoptionForm({...adoptionForm, fee_paid: c})}
                  />
                  <Label className="text-sm">Fee Paid</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={adoptionForm.contract_signed} 
                    onCheckedChange={(c) => setAdoptionForm({...adoptionForm, contract_signed: c})}
                  />
                  <Label className="text-sm">Contract Signed</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={adoptionForm.microchip_transferred} 
                    onCheckedChange={(c) => setAdoptionForm({...adoptionForm, microchip_transferred: c})}
                  />
                  <Label className="text-sm">Microchip Transferred</Label>
                </div>
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea value={adoptionForm.notes} onChange={(e) => setAdoptionForm({...adoptionForm, notes: e.target.value})} />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setAdoptionDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleProcessAdoption} 
                className="bg-green-600 hover:bg-green-700"
                disabled={createNewAdopter ? !newAdopter.name || !newAdopter.email : !selectedAdopter}
              >
                <ClipboardCheck className="w-4 h-4 mr-2" />Complete Adoption
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* EDIT ANIMAL DIALOG */}
      <Dialog open={editAnimalDialogOpen} onOpenChange={setEditAnimalDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />Edit Animal - {editingAnimal?.name}
            </DialogTitle>
          </DialogHeader>
          {editingAnimal && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input value={editingAnimal.name || ''} onChange={(e) => setEditingAnimal({...editingAnimal, name: e.target.value})} />
              </div>
              <div>
                <Label>Species</Label>
                <Select value={editingAnimal.species} onValueChange={(v) => setEditingAnimal({...editingAnimal, species: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Dog">Dog</SelectItem>
                    <SelectItem value="Cat">Cat</SelectItem>
                    <SelectItem value="Bird">Bird</SelectItem>
                    <SelectItem value="Rabbit">Rabbit</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Breed</Label>
                <Input value={editingAnimal.breed || ''} onChange={(e) => setEditingAnimal({...editingAnimal, breed: e.target.value})} />
              </div>
              <div>
                <Label>Gender</Label>
                <Select value={editingAnimal.gender || ''} onValueChange={(v) => setEditingAnimal({...editingAnimal, gender: v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Color</Label>
                <Input value={editingAnimal.color || ''} onChange={(e) => setEditingAnimal({...editingAnimal, color: e.target.value})} />
              </div>
              <div>
                <Label>Weight (lbs)</Label>
                <Input type="number" value={editingAnimal.weight || ''} onChange={(e) => setEditingAnimal({...editingAnimal, weight: e.target.value})} />
              </div>
              <div>
                <Label>Microchip Number</Label>
                <Input value={editingAnimal.microchip_number || ''} onChange={(e) => setEditingAnimal({...editingAnimal, microchip_number: e.target.value})} />
              </div>
              <div>
                <Label>Secondary Microchip</Label>
                <Input value={editingAnimal.secondary_microchip || ''} onChange={(e) => setEditingAnimal({...editingAnimal, secondary_microchip: e.target.value})} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={editingAnimal.status} onValueChange={(v) => setEditingAnimal({...editingAnimal, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="adopted">Adopted</SelectItem>
                    <SelectItem value="fostered">Fostered</SelectItem>
                    <SelectItem value="medical_hold">Medical Hold</SelectItem>
                    <SelectItem value="deceased">Deceased</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Adoption Fee ($)</Label>
                <Input type="number" value={editingAnimal.adoption_fee || ''} onChange={(e) => setEditingAnimal({...editingAnimal, adoption_fee: e.target.value})} />
              </div>
              <div className="col-span-2">
                <Label>Medical Notes</Label>
                <Textarea value={editingAnimal.medical_notes || ''} onChange={(e) => setEditingAnimal({...editingAnimal, medical_notes: e.target.value})} />
              </div>
              <div className="col-span-2">
                <Label>Behavioral Notes</Label>
                <Textarea value={editingAnimal.behavioral_notes || ''} onChange={(e) => setEditingAnimal({...editingAnimal, behavioral_notes: e.target.value})} />
              </div>
              <div className="col-span-2">
                <Label>Special Needs</Label>
                <Textarea value={editingAnimal.special_needs || ''} onChange={(e) => setEditingAnimal({...editingAnimal, special_needs: e.target.value})} />
              </div>
              <DialogFooter className="col-span-2">
                <Button variant="outline" onClick={() => setEditAnimalDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleUpdateAnimal} className="bg-primary hover:bg-primary/90">Save Changes</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* DEATH REPORT DIALOG */}
      <Dialog open={deathReportDialogOpen} onOpenChange={setDeathReportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Skull className="w-5 h-5" />Report Death - {deathReportAnimal?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">This action will mark the animal as deceased and cannot be undone.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date of Death *</Label>
                <Input 
                  type="date" 
                  value={deathReportForm.deceased_date} 
                  onChange={(e) => setDeathReportForm({...deathReportForm, deceased_date: e.target.value})} 
                  required 
                />
              </div>
              <div>
                <Label>Location</Label>
                <Input 
                  value={deathReportForm.location} 
                  onChange={(e) => setDeathReportForm({...deathReportForm, location: e.target.value})} 
                  placeholder="e.g., Shelter, Vet clinic, Foster home"
                />
              </div>
              <div className="col-span-2">
                <Label>Cause of Death *</Label>
                <Select value={deathReportForm.cause_of_death} onValueChange={(v) => setDeathReportForm({...deathReportForm, cause_of_death: v})}>
                  <SelectTrigger><SelectValue placeholder="Select cause" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="natural">Natural Causes / Old Age</SelectItem>
                    <SelectItem value="illness">Illness / Disease</SelectItem>
                    <SelectItem value="injury">Injury / Trauma</SelectItem>
                    <SelectItem value="euthanasia">Euthanasia</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <Checkbox 
                  checked={deathReportForm.euthanized} 
                  onCheckedChange={(c) => setDeathReportForm({...deathReportForm, euthanized: c})}
                />
                <Label>Was the animal euthanized?</Label>
              </div>
              {deathReportForm.euthanized && (
                <div className="col-span-2">
                  <Label>Reason for Euthanasia</Label>
                  <Textarea 
                    value={deathReportForm.euthanasia_reason} 
                    onChange={(e) => setDeathReportForm({...deathReportForm, euthanasia_reason: e.target.value})} 
                    placeholder="Explain the medical/behavioral reasons for euthanasia"
                  />
                </div>
              )}
              <div>
                <Label>Veterinarian</Label>
                <Input 
                  value={deathReportForm.veterinarian} 
                  onChange={(e) => setDeathReportForm({...deathReportForm, veterinarian: e.target.value})} 
                  placeholder="Name of attending vet"
                />
              </div>
              <div className="col-span-2">
                <Label>Additional Notes</Label>
                <Textarea 
                  value={deathReportForm.notes} 
                  onChange={(e) => setDeathReportForm({...deathReportForm, notes: e.target.value})} 
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeathReportDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleReportDeath} 
                className="bg-red-600 hover:bg-red-700"
                disabled={!deathReportForm.deceased_date || !deathReportForm.cause_of_death}
              >
                <Skull className="w-4 h-4 mr-2" />Confirm Death Report
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* PHOTO UPLOAD DIALOG */}
      <Dialog open={photoUploadDialogOpen} onOpenChange={setPhotoUploadDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImagePlus className="w-5 h-5" />Add Photos - {photoUploadAnimal?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {photoUploadAnimal?.photos?.length > 0 && (
              <div>
                <Label className="mb-2 block">Current Photos</Label>
                <div className="flex gap-2 flex-wrap">
                  {photoUploadAnimal.photos.map((photo, idx) => (
                    <div key={idx} className="relative group">
                      <img src={photo} alt={`Photo ${idx + 1}`} className="w-20 h-20 object-cover rounded" />
                      <button 
                        onClick={() => handleDeletePhoto(photoUploadAnimal.animal_id, idx)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <PhotoUploader 
              photos={photoUploadAnimal?.photos?.map((url, idx) => ({ id: `photo_${idx}`, url, type: 'image' })) || []}
              onPhotosChange={(photos) => {
                const photoUrls = photos.map(p => p.url || p);
                handleAddPhotos(photoUrls);
              }}
              maxPhotos={5}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* EVENT DIALOG */}
      <Dialog open={eventDialogOpen || !!editingEvent} onOpenChange={(open) => {
        if (!open) {
          setEventDialogOpen(false);
          setEditingEvent(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Edit Event' : 'Create Event'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={editingEvent ? handleUpdateEvent : handleCreateEvent} className="space-y-4">
            <div>
              <Label>Event Title *</Label>
              <Input 
                value={editingEvent?.title || newEvent.title}
                onChange={(e) => editingEvent 
                  ? setEditingEvent({...editingEvent, title: e.target.value})
                  : setNewEvent({...newEvent, title: e.target.value})
                }
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Event Date *</Label>
                <Input 
                  type="datetime-local"
                  value={editingEvent?.event_date?.slice(0, 16) || newEvent.event_date}
                  onChange={(e) => editingEvent 
                    ? setEditingEvent({...editingEvent, event_date: e.target.value})
                    : setNewEvent({...newEvent, event_date: e.target.value})
                  }
                  required
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input 
                  type="datetime-local"
                  value={editingEvent?.end_date?.slice(0, 16) || newEvent.end_date}
                  onChange={(e) => editingEvent 
                    ? setEditingEvent({...editingEvent, end_date: e.target.value})
                    : setNewEvent({...newEvent, end_date: e.target.value})
                  }
                />
              </div>
            </div>
            <div>
              <Label>Event Type</Label>
              <Select
                value={editingEvent?.event_type || newEvent.event_type}
                onValueChange={(v) => editingEvent 
                  ? setEditingEvent({...editingEvent, event_type: v})
                  : setNewEvent({...newEvent, event_type: v})
                }
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="adoption_event">Adoption Event</SelectItem>
                  <SelectItem value="fundraiser">Fundraiser</SelectItem>
                  <SelectItem value="volunteer">Volunteer Day</SelectItem>
                  <SelectItem value="education">Education/Workshop</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Location</Label>
              <Input 
                value={editingEvent?.location || newEvent.location}
                onChange={(e) => editingEvent 
                  ? setEditingEvent({...editingEvent, location: e.target.value})
                  : setNewEvent({...newEvent, location: e.target.value})
                }
                placeholder="Event venue"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input 
                value={editingEvent?.address || newEvent.address}
                onChange={(e) => editingEvent 
                  ? setEditingEvent({...editingEvent, address: e.target.value})
                  : setNewEvent({...newEvent, address: e.target.value})
                }
                placeholder="Full address"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea 
                value={editingEvent?.description || newEvent.description}
                onChange={(e) => editingEvent 
                  ? setEditingEvent({...editingEvent, description: e.target.value})
                  : setNewEvent({...newEvent, description: e.target.value})
                }
                rows={3}
              />
            </div>
            <div>
              <Label>Registration URL (optional)</Label>
              <Input 
                type="url"
                value={editingEvent?.registration_url || newEvent.registration_url}
                onChange={(e) => editingEvent 
                  ? setEditingEvent({...editingEvent, registration_url: e.target.value})
                  : setNewEvent({...newEvent, registration_url: e.target.value})
                }
                placeholder="https://..."
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                checked={editingEvent?.is_published || newEvent.is_published}
                onCheckedChange={(c) => editingEvent 
                  ? setEditingEvent({...editingEvent, is_published: c})
                  : setNewEvent({...newEvent, is_published: c})
                }
              />
              <Label>Publish to website immediately</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setEventDialogOpen(false);
                setEditingEvent(null);
              }}>Cancel</Button>
              <Button type="submit">{editingEvent ? 'Update Event' : 'Create Event'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* PUBLISH TO WEBSITE DIALOG */}
      <Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-500" />
              Publish {publishAnimal?.name} to Website
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-stone-600">
              Choose how this pet should appear on your public website:
            </p>
            <div className="space-y-3">
              <div 
                className={`p-4 border rounded-lg cursor-pointer transition-colors ${publishType === 'adoptable' ? 'border-green-500 bg-green-50' : 'hover:border-stone-300'}`}
                onClick={() => setPublishType('adoptable')}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full border-2 ${publishType === 'adoptable' ? 'border-green-500 bg-green-500' : 'border-stone-300'}`}>
                    {publishType === 'adoptable' && <div className="w-2 h-2 bg-white rounded-full m-0.5" />}
                  </div>
                  <span className="font-medium">Adoptable Pet</span>
                </div>
                <p className="text-sm text-stone-500 mt-1 ml-6">
                  Pet will appear on the &quot;Pets for Adoption&quot; page with adoption fee info.
                </p>
              </div>
              <div 
                className={`p-4 border rounded-lg cursor-pointer transition-colors ${publishType === 'found' ? 'border-amber-500 bg-amber-50' : 'hover:border-stone-300'}`}
                onClick={() => setPublishType('found')}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full border-2 ${publishType === 'found' ? 'border-amber-500 bg-amber-500' : 'border-stone-300'}`}>
                    {publishType === 'found' && <div className="w-2 h-2 bg-white rounded-full m-0.5" />}
                  </div>
                  <span className="font-medium">Found Pet</span>
                </div>
                <p className="text-sm text-stone-500 mt-1 ml-6">
                  Pet will appear on the &quot;Found Pets&quot; page to help find its owner.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishDialogOpen(false)}>Cancel</Button>
            <Button onClick={confirmPublishToWebsite} className="bg-blue-600 hover:bg-blue-700">
              <Globe className="w-4 h-4 mr-2" />Publish to Website
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShelterDashboard;
